import { Check, ChevronDown, Loader2, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { Input } from '../../../components/ui/input.js';
import { Label } from '../../../components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.js';
import { cn } from '../../../lib/utils.js';
import { useLicense } from '../../common/license-context.js';
import { useBackupJobs, useTriggerPlanningRestore, type PlanningBackupJob } from '../hooks.js';

/**
 * Modal for triggering a Planning restore. Mirrors the dropdown
 * pattern in `CreateBackupDialog`:
 *
 *   - **Planning instance**: `Select` populated from the license's
 *     primary + secondary workfront-configs (engine matches by name
 *     OR UUID since #157).
 *   - **Archive**: `DropdownMenu` of this license's Completed backups (`useBackupJobs()`). The Backups tab can preselect one through `initialBackupId`. Restores only ever name a backup id; there is no URL input (V-01).
 *   - **Scope (optional)**: free-text JSON for now — restore phases
 *     don't yet narrow by workspaceIds. Ship-and-ignore until the
 *     phase pipeline knows how to thread the filter through ctx.
 *   - **Run name**: auto-pre-populated from `{instance} —
 *     {archive finishedAt}`; operator can edit, with a "Reset to
 *     auto" link to snap back.
 *
 * Engine contract: `instance` + `sourceBackupId` are required.
 */
interface CreateRestoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialBackupId?: string;
  onTriggered?: () => void;
}

const NAME_MAX = 50;

export function CreateRestoreDialog({
  open,
  onOpenChange,
  initialBackupId,
  onTriggered,
}: CreateRestoreDialogProps): JSX.Element {
  const { trigger, isPending, error } = useTriggerPlanningRestore();
  const { details } = useLicense();
  const { data: backupJobs = [], isLoading: backupsLoading } = useBackupJobs();

  const instanceOptions = useMemo<ReadonlyArray<{ value: string; label: string }>>(() => {
    if (!details) return [];
    const out: Array<{ value: string; label: string }> = [];
    if (details.primaryWorkfrontConfig) {
      out.push({
        value: details.primaryWorkfrontConfig.name,
        label: details.primaryWorkfrontConfig.name,
      });
    }
    for (const sec of details.secondaryWorkfrontConfigs) {
      out.push({ value: sec.name, label: sec.name });
    }
    return out;
  }, [details]);

  const [instance, setInstance] = useState('');
  useEffect(() => {
    if (!open) return;
    if (instance) return;
    setInstance(instanceOptions[0]?.value ?? '');
  }, [open, instance, instanceOptions]);

  const archiveOptions = useMemo<ReadonlyArray<PlanningBackupJob>>(
    () => (backupJobs ?? []).filter((j) => j.status === 'Completed' && !!j.fileUrl),
    [backupJobs],
  );

  const [sourceBackupId, setSourceBackupId] = useState(initialBackupId ?? '');
  useEffect(() => {
    if (!open) return;
    if (initialBackupId && !sourceBackupId) setSourceBackupId(initialBackupId);
  }, [open, initialBackupId, sourceBackupId]);

  const selectedArchive = useMemo(
    () => archiveOptions.find((j) => j.id === sourceBackupId),
    [archiveOptions, sourceBackupId],
  );

  const [scopeJson, setScopeJson] = useState('');
  const scopeJsonValid = (() => {
    if (!scopeJson.trim()) return true;
    try {
      JSON.parse(scopeJson);
      return true;
    } catch {
      return false;
    }
  })();

  const [name, setName] = useState('');
  const [autoName, setAutoName] = useState(true);

  const generatedName = useMemo(() => {
    if (!instance) return '';
    const parts: string[] = [instance];
    const stamp =
      selectedArchive?.finishedAt ?? selectedArchive?.createdAt ?? new Date().toISOString();
    parts.push(new Date(stamp).toLocaleString());
    // ASCII hyphen — see CreateBackupDialog for the engine regex
    // (printable ASCII only) that rejects em-dash.
    return parts.join(' - ').slice(0, NAME_MAX);
  }, [instance, selectedArchive]);

  useEffect(() => {
    if (autoName) setName(generatedName);
  }, [autoName, generatedName]);

  useEffect(() => {
    if (!open) {
      setSourceBackupId('');
      setScopeJson('');
      setName('');
      setAutoName(true);
    }
  }, [open]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    let scope: Record<string, unknown> | undefined;
    if (scopeJson.trim()) {
      try {
        scope = JSON.parse(scopeJson) as Record<string, unknown>;
      } catch {
        return;
      }
    }
    await trigger({
      instance: instance.trim(),
      sourceBackupId,
      ...(scope ? { scope } : {}),
      ...(name.trim() ? { name: name.trim() } : {}),
    });
    onOpenChange(false);
    onTriggered?.();
  };

  const submitDisabled = !instance.trim() || !selectedArchive || !scopeJsonValid || isPending;

  const archiveTriggerLabel = (() => {
    if (backupsLoading) return 'Loading…';
    if (!selectedArchive) return 'Pick an archive';
    const stamp = selectedArchive.finishedAt ?? selectedArchive.createdAt;
    return `${selectedArchive.instance} — ${new Date(stamp).toLocaleString()}`;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Upload className="h-4 w-4" /> Trigger Planning restore
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <section>
            <Label className="mb-1 block text-sm text-foreground/80" htmlFor="restore-instance">
              Planning instance
            </Label>
            <Select value={instance} onValueChange={setInstance}>
              <SelectTrigger
                id="restore-instance"
                className="border-foreground/15 bg-foreground/[0.04] text-foreground"
              >
                <SelectValue placeholder="Pick an instance" />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {instanceOptions.length === 0 ? (
                  <SelectItem value="__none__" disabled>
                    No workfront-config on this license
                  </SelectItem>
                ) : (
                  instanceOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-foreground/40">
              Sourced from the license's primary + secondary workfront-configs.
            </p>
          </section>

          <section>
            <Label className="mb-1 block text-sm text-foreground/80">Archive</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'inline-flex w-full items-center justify-between rounded-md border border-foreground/15 bg-foreground/[0.04] px-3 py-2 text-sm text-foreground/80 transition-colors hover:border-foreground/30',
                    backupsLoading && 'opacity-60',
                  )}
                  disabled={backupsLoading}
                >
                  <span className="flex items-center gap-2 truncate">
                    {backupsLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground/50" />
                    ) : null}
                    <span className="truncate">{archiveTriggerLabel}</span>
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-foreground/50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="z-[10001] max-h-[40vh] w-[--radix-dropdown-menu-trigger-width] overflow-y-auto"
              >
                <DropdownMenuLabel>Completed backups</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {archiveOptions.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-foreground/40">
                    No completed backups yet.
                  </p>
                ) : (
                  <DropdownMenuRadioGroup
                    value={sourceBackupId}
                    onValueChange={(v) => setSourceBackupId(v)}
                  >
                    {archiveOptions.map((j) => {
                      const stamp = j.finishedAt ?? j.createdAt;
                      return (
                        <DropdownMenuRadioItem key={j.id} value={j.id}>
                          <span className="flex w-full items-center justify-between gap-2">
                            <span className="truncate">{j.instance}</span>
                            <span className="shrink-0 text-[0.65rem] uppercase tracking-wider text-foreground/40">
                              {new Date(stamp).toLocaleString()}
                            </span>
                          </span>
                        </DropdownMenuRadioItem>
                      );
                    })}
                  </DropdownMenuRadioGroup>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </section>

          <section>
            <Label className="mb-1 block text-sm text-foreground/80" htmlFor="restore-scope">
              Scope (optional JSON)
            </Label>
            <Input
              id="restore-scope"
              value={scopeJson}
              onChange={(e) => setScopeJson(e.target.value)}
              placeholder='{"workspaceIds":["…"]}'
              className={cn(
                'border-foreground/15 bg-foreground/[0.04] font-mono text-xs text-foreground placeholder:text-foreground/30',
                !scopeJsonValid && 'border-rose-400/60',
              )}
            />
            {!scopeJsonValid ? (
              <p className="mt-1 text-xs text-rose-400">Not valid JSON.</p>
            ) : (
              <p className="mt-1 text-xs text-foreground/40">
                Leave blank to restore everything in the archive.
              </p>
            )}
          </section>

          <section>
            <Label className="mb-1 block text-sm text-foreground/80" htmlFor="restore-name">
              Run name (optional)
            </Label>
            <Input
              id="restore-name"
              value={name}
              onChange={(e) => {
                setAutoName(false);
                setName(e.target.value);
              }}
              placeholder={generatedName || 'Disaster-recovery test'}
              maxLength={NAME_MAX}
              className="border-foreground/15 bg-foreground/[0.04] text-foreground placeholder:text-foreground/30"
            />
            <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground/40">
              {autoName ? (
                <span className="inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> Auto-generated from instance + archive timestamp
                </span>
              ) : (
                <button
                  type="button"
                  className="text-foreground/60 hover:underline"
                  onClick={() => {
                    setAutoName(true);
                    setName(generatedName);
                  }}
                >
                  Reset to auto
                </button>
              )}
            </p>
          </section>

          {error ? (
            <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-2 text-xs text-rose-800 dark:text-rose-200">
              {error.message}
            </div>
          ) : null}

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-foreground/15 text-foreground/70"
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitDisabled}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              {isPending ? 'Triggering…' : 'Trigger restore'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
